<template>
  <transition name="templates-floating-fade">
    <div
      v-if="show"
      class="templates-floating-overlay"
      :class="{ 'dragging-active': !isInitialPosition }"
    >
      <div
        ref="modalRef"
        class="templates-floating-panel"
        :class="{ 'is-dragging': isDragging }"
        :style="style"
        v-show="!isPreviewOpen"
        role="dialog"
        aria-label="Template Viewer"
        @click.stop
        @mousedown="startDrag"
        @touchstart="startDrag"
      >
        <div class="floating-header">
          <div class="floating-header__text">
            <h6 class="floating-header__title">Template Viewer</h6>
            <p class="floating-header__subtitle">Drag to move · browse and copy templates</p>
          </div>
          <div class="floating-header__actions">
            <div class="drag-handle" title="Drag to move">
              <Icon icon="mdi:drag" width="16" height="16" />
            </div>
            <button
              type="button"
              class="floating-close-btn"
              aria-label="Close templates"
              @click="$emit('close')"
            >
              <Icon icon="mdi:close" width="18" height="18" />
            </button>
          </div>
        </div>
        <div class="floating-body">
          <TemplateViewerModal
            :hide-header="true"
            @close="$emit('close')"
            @preview-change="isPreviewOpen = $event"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { Icon } from "@iconify/vue";
import TemplateViewerModal from "./TemplateViewerModal.vue";
import { useDraggable } from "@/composables/ui/useDraggable";

defineProps<{ show: boolean }>();
defineEmits<{ close: [] }>();

const isPreviewOpen = ref(false);

const { modalRef, isDragging, isInitialPosition, style, startDrag } = useDraggable({
  initialWidth: 480,
  initialHeight: 640,
  excludedSelectors: [
    ".floating-body",
    "button",
    "a",
    "input",
    "textarea",
    "[contenteditable]",
    ".ql-toolbar",
    ".ql-editor",
  ],
});
</script>

<style scoped>
.templates-floating-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 24px;
  z-index: 10060;
  pointer-events: none;
}

.templates-floating-overlay.dragging-active {
  align-items: flex-start;
  justify-content: flex-start;
}

.templates-floating-panel {
  background: #fff;
  border-radius: 12px;
  width: 480px;
  max-width: 90vw;
  max-height: 88vh;
  height: 640px;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.06),
    0 12px 32px rgba(15, 23, 42, 0.16),
    0 30px 80px rgba(15, 23, 42, 0.22);
  transition: box-shadow 0.2s ease;
  pointer-events: auto;
  position: relative;
  overflow: hidden;
}

.templates-floating-panel.is-dragging {
  box-shadow:
    0 2px 4px rgba(15, 23, 42, 0.08),
    0 20px 48px rgba(15, 23, 42, 0.24),
    0 40px 100px rgba(15, 23, 42, 0.32);
  cursor: grabbing;
  transition: none;
}

.floating-header {
  background: linear-gradient(90deg, #2d3bff 0%, #4f63ff 100%);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  cursor: grab;
  user-select: none;
}

.floating-header:active {
  cursor: grabbing;
}

.floating-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.floating-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
}

.floating-header__subtitle {
  margin: 0;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.35;
}

.floating-header__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.drag-handle {
  color: rgba(255, 255, 255, 0.75);
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition:
    background 0.2s ease,
    color 0.2s ease;
  pointer-events: none;
}

.floating-header:hover .drag-handle {
  color: #fff;
}

.floating-close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease;
}

.floating-close-btn:hover {
  background: rgba(255, 255, 255, 0.35);
}

.floating-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: default;
}

.templates-floating-fade-enter-active,
.templates-floating-fade-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.templates-floating-fade-enter-from,
.templates-floating-fade-leave-to {
  opacity: 0;
}

.templates-floating-fade-enter-from .templates-floating-panel,
.templates-floating-fade-leave-to .templates-floating-panel {
  transform: scale(0.96);
}
</style>
